import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useStudentProfileStore } from '../../stateManagers/stores/studentProfileStore';

/* ─── Design tokens (from Assignments) ─────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldLight:   '#ffc93c',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  bg:          '#f5f7fa',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
  amber:       '#f59e0b',
  amberDim:    'rgba(245,158,11,0.12)',
  amberBorder: 'rgba(245,158,11,0.35)',
};

/* ─── Helper sub-components ─────────────────────────────────────────── */

const SectionCard: React.FC<{ icon: string; title: string; subtitle: string; children: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
  <div style={{
    background:    C.white,
    borderRadius:  '16px',
    overflow:      'hidden',
    border:        `1px solid ${C.border}`,
    boxShadow:     '0 2px 8px rgba(14,34,93,0.06)',
  }}>
    <div style={{ height: '3px', background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 100%)` }} />
    <div style={{ padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
        <div style={{
          width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
          background: 'rgba(14,34,93,0.08)',
          border: `1.5px solid ${C.goldBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className={`pi ${icon}`} style={{ fontSize: '1rem', color: C.navy }} />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>{title}</p>
          <p style={{ margin: '2px 0 0 0', fontSize: '0.72rem', color: C.muted }}>{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  </div>
);

const Field: React.FC<{ label: string; value?: string | number | null; mono?: boolean; full?: boolean }> = ({ label, value, mono, full }) => (
  <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
    <p style={{
      margin: '0 0 4px 0', fontSize: '0.62rem', color: C.muted,
      fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {label}
    </p>
    <p style={{
      margin: 0, fontSize: '0.85rem', fontWeight: '600',
      color: value ? C.body : C.border,
      fontFamily: mono ? 'monospace' : undefined,
    }}>
      {value ?? '—'}
    </p>
  </div>
);

const Chip: React.FC<{ icon: string; label: string; active?: boolean; color?: string }> = ({ icon, label, active, color }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '3px 10px', borderRadius: '20px',
    background: active ? C.navy : (color ? `${color}18` : C.goldDim),
    border: `1px solid ${active ? C.navy : (color ? `${color}40` : C.goldBorder)}`,
    fontSize: '0.7rem', fontWeight: '700',
    color: active ? C.gold : (color ?? C.navy),
  }}>
    <i className={`pi ${icon}`} style={{ fontSize: '0.65rem' }} />
    {label}
  </div>
);

const FieldGrid: React.FC<{ children: React.ReactNode; cols?: number }> = ({ children, cols = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px 20px' }}>
    {children}
  </div>
);

/* ─── Main Component ─────────────────────────────────────────────────── */
const StudentProfile: React.FC = () => {
  const navigate = useNavigate();
  const { profile, isLoading, error, fetchProfile } = useStudentProfileStore();

  useEffect(() => { fetchProfile(); }, []);

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '360px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="3" animationDuration=".8s" />
          </div>
          <p style={{ margin: 0, color: C.navy, fontSize: '0.92rem', fontWeight: '600' }}>Loading profile…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error || !profile) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '320px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`,
        padding: '52px 26px', textAlign: 'center',
      }}>
        <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: C.redDim, border: `2px solid ${C.redBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.6rem', color: C.red }} />
        </div>
        <p style={{ margin: '0 0 6px 0', fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Failed to load profile</p>
        <p style={{ margin: '0 0 18px 0', color: C.muted, fontSize: '0.85rem' }}>{error || 'Profile not found'}</p>
        <button
          onClick={fetchProfile}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 20px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.navy, fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-refresh" style={{ fontSize: '0.8rem' }} /> Try Again
        </button>
      </div>
    );
  }

  const { institute, branch, enrollments, is_enrolled, total_courses } = profile;

  return (
    <>
      <style>{`
        @keyframes spFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .sp-item { animation: spFadeIn 0.25s ease-out both; }
        .sp-action-btn { transition: all 0.15s ease; }
        .sp-action-btn:hover { background: ${C.navy} !important; color: ${C.gold} !important; border-color: ${C.navy} !important; }
        .sp-action-btn-primary:hover { background: ${C.goldLight} !important; }
        .sp-enr-row:hover { background: rgba(14,34,93,0.03); }
      `}</style>

      <div style={{ background: C.bg, minHeight: '100%' }}>

        {/* ── Hero banner ──────────────────────────────────────────── */}
        <div style={{
          background:   `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 60%, ${C.navyDark} 100%)`,
          borderRadius: '20px',
          padding:      '28px 32px',
          marginBottom: '24px',
          color:        C.white,
          position:     'relative',
          overflow:     'hidden',
          boxShadow:    '0 4px 18px rgba(14,34,93,0.25)',
        }}>
          {/* Glow blob */}
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Top row: avatar + name + tags */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '22px' }}>
              {/* Avatar */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {profile.profilePicture?.cdn_url ? (
                  <img
                    src={profile.profilePicture.cdn_url}
                    alt={profile.name}
                    style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${C.goldBorder}` }}
                  />
                ) : (
                  <div style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: C.goldDim, border: `3px solid ${C.goldBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.8rem', fontWeight: '700', color: C.gold,
                  }}>
                    {profile.name.charAt(0).toUpperCase()}
                  </div>
                )}
                {/* Status dot */}
                <div style={{
                  position: 'absolute', bottom: '3px', right: '3px',
                  width: '14px', height: '14px', borderRadius: '50%',
                  background: profile.status === 'active' ? C.green : C.red,
                  border: `2px solid ${C.navyDark}`,
                }} />
              </div>

              {/* Name block */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '4px 12px', background: C.goldDim,
                  border: `1px solid ${C.goldBorder}`,
                  borderRadius: '20px', marginBottom: '8px',
                  fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px',
                  textTransform: 'uppercase', color: C.gold,
                }}>
                  <i className="pi pi-user" style={{ fontSize: '0.7rem' }} />
                  Student Profile
                </div>
                <h1 style={{ margin: '0 0 4px 0', fontSize: '1.55rem', fontWeight: '700', color: C.white, letterSpacing: '-0.4px' }}>
                  {profile.name}
                </h1>
                <p style={{ margin: '0 0 10px 0', color: 'rgba(255,255,255,0.55)', fontSize: '0.83rem' }}>
                  {profile.email}
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Chip
                    icon={profile.status === 'active' ? 'pi-check-circle' : 'pi-times-circle'}
                    label={profile.status.toUpperCase()}
                    color={profile.status === 'active' ? C.green : C.red}
                  />
                  {profile.student_custom_id && (
                    <Chip icon="pi-id-card" label={`ID: ${profile.student_custom_id}`} />
                  )}
                  <Chip
                    icon={is_enrolled ? 'pi-graduation-cap' : 'pi-info-circle'}
                    label={`${total_courses} Course${total_courses !== 1 ? 's' : ''}`}
                    color={is_enrolled ? C.green : C.amber}
                  />
                </div>
              </div>

              {/* Meta top-right */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace' }}>
                  {profile.student_id}
                </p>
                <p style={{ margin: '6px 0 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                  Joined {formatDate(profile.created_at)}
                </p>
              </div>
            </div>

            {/* Quick stat pills */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {[
                { label: 'Mobile',   value: profile.mobileNumber,           icon: 'pi-mobile' },
                { label: 'Gender',   value: profile.gender,                 icon: 'pi-user' },
                { label: 'Category', value: profile.category,               icon: 'pi-tag' },
                { label: 'Location', value: `${profile.district}, ${profile.state}`, icon: 'pi-map-marker' },
              ].map(({ label, value, icon }) => (
                <div key={label} style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: '12px', padding: '9px 14px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <i className={`pi ${icon}`} style={{ fontSize: '0.8rem', color: C.gold }} />
                  <div>
                    <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700' }}>
                      {label}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '600', color: C.white }}>
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Main grid: 2fr + 1fr ──────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '22px' }}>

          {/* ── Left column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Personal Details */}
            <div className="sp-item" style={{ animationDelay: '0ms' }}>
              <SectionCard icon="pi-user" title="Personal Details" subtitle="Basic information">
                <FieldGrid>
                  <Field label="Date of Birth"  value={formatDate(profile.dateOfBirth)} />
                  <Field label="Aadhar Number"  value={profile.aadharNumber} mono />
                  <Field label="Blood Group"    value={profile.bloodGroup} />
                  <Field label="Whatsapp"       value={profile.whatsappNumber} />
                  <Field label="Pin Code"       value={profile.pinCode} />
                  <Field label="Village / Town" value={profile.villageTown} />
                  {profile.tehsil && <Field label="Tehsil"    value={profile.tehsil} />}
                  {profile.wardNo && <Field label="Ward No." value={profile.wardNo} />}
                </FieldGrid>

                {profile.previousIllness && (
                  <div style={{
                    marginTop: '14px', padding: '10px 14px',
                    background: C.amberDim, border: `1px solid ${C.amberBorder}`,
                    borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px',
                  }}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.9rem', color: C.amber, marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p style={{ margin: '0 0 3px 0', fontSize: '0.65rem', fontWeight: '700', color: C.amber, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Previous Illness</p>
                      <p style={{ margin: 0, fontSize: '0.83rem', color: C.body }}>{profile.previousIllness}</p>
                    </div>
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Family Information */}
            <div className="sp-item" style={{ animationDelay: '60ms' }}>
              <SectionCard icon="pi-heart" title="Family Information" subtitle="Parent and guardian details">
                <FieldGrid>
                  <Field label="Father's Name"       value={profile.fatherName} />
                  <Field label="Father's Occupation" value={profile.fatherOccupation} />
                  <Field label="Father's Mobile"     value={profile.fatherMobile} />
                  <Field label="Father's Whatsapp"   value={profile.fatherWhatsapp} />
                  <Field label="Mother's Name"       value={profile.motherName} />
                  <Field label="Mother's Occupation" value={profile.motherOccupation} />
                  <Field label="Mother's Mobile"     value={profile.motherMobile} />
                  <Field label="Mother's Whatsapp"   value={profile.motherWhatsapp} />
                </FieldGrid>

                {(profile.fatherIncome || profile.motherIncome) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '14px' }}>
                    {profile.fatherIncome ? (
                      <div style={{ padding: '10px 14px', background: C.greenDim, border: `1px solid ${C.greenBorder}`, borderRadius: '10px' }}>
                        <p style={{ margin: '0 0 3px 0', fontSize: '0.62rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Father's Income</p>
                        <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: '700', color: C.green }}>₹{profile.fatherIncome.toLocaleString('en-IN')}</p>
                      </div>
                    ) : null}
                    {profile.motherIncome ? (
                      <div style={{ padding: '10px 14px', background: C.greenDim, border: `1px solid ${C.greenBorder}`, borderRadius: '10px' }}>
                        <p style={{ margin: '0 0 3px 0', fontSize: '0.62rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Mother's Income</p>
                        <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: '700', color: C.green }}>₹{profile.motherIncome.toLocaleString('en-IN')}</p>
                      </div>
                    ) : null}
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Education History */}
            <div className="sp-item" style={{ animationDelay: '120ms' }}>
              <SectionCard icon="pi-book" title="Education History" subtitle="Previous academic details">
                <FieldGrid>
                  <Field label="Previous Class"  value={profile.previousClass} />
                  <Field label="Year of Passing" value={profile.yearOfPassing} />
                  <Field label="School Board"    value={profile.schoolBoard} full />
                </FieldGrid>
                {profile.totalMarks && profile.marksObtained ? (
                  <div style={{
                    marginTop: '14px', padding: '12px 16px',
                    background: 'rgba(14,34,93,0.05)',
                    border: `1px solid ${C.goldBorder}`,
                    borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div>
                      <p style={{ margin: '0 0 3px 0', fontSize: '0.62rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Marks Obtained</p>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>
                        {profile.marksObtained} <span style={{ fontSize: '0.8rem', color: C.sub, fontWeight: '500' }}>/ {profile.totalMarks}</span>
                      </p>
                    </div>
                    <div style={{
                      padding: '6px 14px', borderRadius: '20px',
                      background: C.goldDim, border: `1px solid ${C.goldBorder}`,
                      fontSize: '1rem', fontWeight: '700', color: C.navy,
                    }}>
                      {Math.round((profile.marksObtained / profile.totalMarks) * 100)}%
                    </div>
                  </div>
                ) : null}
              </SectionCard>
            </div>

            {/* Enrollments */}
            {enrollments.length > 0 && (
              <div className="sp-item" style={{ animationDelay: '180ms' }}>
                <SectionCard icon="pi-graduation-cap" title="Enrollments" subtitle={`${total_courses} course${total_courses !== 1 ? 's' : ''} enrolled`}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {enrollments.map((enr) => (
                      <div
                        key={enr.enrollment_id}
                        className="sp-enr-row"
                        style={{
                          padding: '14px 16px', borderRadius: '12px',
                          border: `1px solid ${C.border}`, background: C.white,
                          transition: 'background 0.15s',
                        }}
                      >
                        {/* Top accent bar */}
                        <div style={{ height: '2px', background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 100%)`, borderRadius: '2px', marginBottom: '12px', width: '48px' }} />

                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                          <div>
                            <p style={{ margin: '0 0 3px 0', fontSize: '0.92rem', fontWeight: '700', color: C.navy }}>
                              {enr.course_name ?? '—'}
                            </p>
                            {enr.course_title && (
                              <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub }}>{enr.course_title}</p>
                            )}
                          </div>
                          {enr.batch_name && (
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              padding: '3px 10px', borderRadius: '20px',
                              background: 'rgba(14,34,93,0.06)', border: `1px solid rgba(14,34,93,0.15)`,
                              fontSize: '0.7rem', fontWeight: '700', color: C.navy, flexShrink: 0,
                            }}>
                              <i className="pi pi-users" style={{ fontSize: '0.65rem' }} />
                              {enr.batch_name}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', borderTop: `1px solid ${C.border}`, paddingTop: '10px' }}>
                          <div>
                            <p style={{ margin: '0 0 3px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Enrollment No.</p>
                            <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '600', color: enr.enrollment_no ? C.body : C.border, fontFamily: 'monospace' }}>{enr.enrollment_no ?? '—'}</p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 3px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Batch Time</p>
                            <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '600', color: enr.batch_time ? C.body : C.border }}>{enr.batch_time ?? '—'}</p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 3px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admission Date</p>
                            <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '600', color: C.body }}>{formatDate(enr.date_of_admission)}</p>
                          </div>
                          {(enr.start_month || enr.start_year) && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <p style={{ margin: '0 0 3px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duration</p>
                              <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '600', color: C.body }}>
                                {[enr.start_month, enr.start_year].filter(Boolean).join(' ')} – {[enr.end_month, enr.end_year].filter(Boolean).join(' ') || '—'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            )}
          </div>

          {/* ── Right column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Enrollment Status card */}
            <div className="sp-item" style={{ animationDelay: '0ms' }}>
              <div style={{
                background: C.white, borderRadius: '16px', overflow: 'hidden',
                border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
              }}>
                <div style={{ height: '3px', background: is_enrolled ? `linear-gradient(90deg, ${C.green} 0%, #4ade80 100%)` : `linear-gradient(90deg, ${C.amber} 0%, ${C.goldLight} 100%)` }} />
                <div style={{ padding: '20px 22px' }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '18px', borderRadius: '12px', textAlign: 'center',
                    background: is_enrolled ? C.greenDim : C.amberDim,
                    border: `1px solid ${is_enrolled ? C.greenBorder : C.amberBorder}`,
                  }}>
                    <i
                      className={`pi ${is_enrolled ? 'pi-check-circle' : 'pi-info-circle'}`}
                      style={{ fontSize: '1.8rem', color: is_enrolled ? C.green : C.amber, marginBottom: '8px' }}
                    />
                    <p style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: '700', color: is_enrolled ? C.green : C.amber }}>
                      {is_enrolled ? 'Enrolled' : 'Not Enrolled'}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub }}>
                      {total_courses} course{total_courses !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Institute & Branch */}
            <div className="sp-item" style={{ animationDelay: '60ms' }}>
              <SectionCard icon="pi-building" title="Institute" subtitle="Assigned institute and branch">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Institute */}
                  <div style={{
                    padding: '13px 15px', borderRadius: '10px',
                    background: 'rgba(14,34,93,0.05)', border: `1px solid ${C.goldBorder}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                      <i className="pi pi-home" style={{ fontSize: '0.85rem', color: C.navy, marginTop: '2px' }} />
                      <div>
                        <p style={{ margin: '0 0 2px 0', fontSize: '0.88rem', fontWeight: '700', color: C.navy }}>{institute.name}</p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: C.sub, lineHeight: 1.4 }}>{institute.address}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: `1px solid ${C.border}`, paddingTop: '8px' }}>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: C.body, display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="pi pi-phone" style={{ fontSize: '0.7rem', color: C.muted }} />
                        {institute.contact_no}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: C.body, display: 'flex', alignItems: 'center', gap: '5px', wordBreak: 'break-all' }}>
                        <i className="pi pi-envelope" style={{ fontSize: '0.7rem', color: C.muted }} />
                        {institute.email}
                      </p>
                    </div>
                  </div>

                  {/* Branch */}
                  <div style={{
                    padding: '13px 15px', borderRadius: '10px',
                    background: 'rgba(14,34,93,0.03)', border: `1px solid ${C.border}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <i className="pi pi-map-marker" style={{ fontSize: '0.85rem', color: C.gold, marginTop: '2px' }} />
                      <div>
                        <p style={{ margin: '0 0 2px 0', fontSize: '0.88rem', fontWeight: '700', color: C.navy }}>{branch.name}</p>
                        <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', color: C.sub, lineHeight: 1.4 }}>{branch.address}</p>
                        {branch.email && (
                          <p style={{ margin: 0, fontSize: '0.75rem', color: C.body, display: 'flex', alignItems: 'center', gap: '5px', wordBreak: 'break-all' }}>
                            <i className="pi pi-envelope" style={{ fontSize: '0.7rem', color: C.muted }} />
                            {branch.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Linked Devices */}
            {profile.total_devices > 0 && (
              <div className="sp-item" style={{ animationDelay: '120ms' }}>
                <div style={{
                  background: C.white, borderRadius: '16px', overflow: 'hidden',
                  border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
                  padding: '18px 22px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: 'rgba(14,34,93,0.08)', border: `1.5px solid ${C.goldBorder}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="pi pi-mobile" style={{ fontSize: '1rem', color: C.navy }} />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '700', color: C.navy }}>Linked Devices</p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.sub }}>
                        {profile.total_devices} device{profile.total_devices !== 1 ? 's' : ''} registered
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Action buttons ───────────────────────────────────────── */}
        <div style={{
          background:   C.white,
          borderRadius: '16px',
          border:       `1px solid ${C.border}`,
          boxShadow:    '0 1px 4px rgba(14,34,93,0.05)',
          padding:      '16px 20px',
          display:      'flex',
          gap:          '10px',
          flexWrap:     'wrap',
          justifyContent: 'center',
        }}>
          <button
            className="sp-action-btn sp-action-btn-primary"
            onClick={() => navigate('/assignments')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 22px', borderRadius: '10px', cursor: 'pointer',
              background: C.navy, border: `1.5px solid ${C.navy}`,
              color: C.gold, fontSize: '0.83rem', fontWeight: '700',
            }}
          >
            <i className="pi pi-file-edit" style={{ fontSize: '0.8rem' }} />
            Assignments
          </button>
          <button
            className="sp-action-btn"
            onClick={() => navigate('/attendance')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 22px', borderRadius: '10px', cursor: 'pointer',
              background: 'transparent', border: `1.5px solid ${C.border}`,
              color: C.navy, fontSize: '0.83rem', fontWeight: '700',
            }}
          >
            <i className="pi pi-calendar-check" style={{ fontSize: '0.8rem' }} />
            Attendance
          </button>
          <button
            className="sp-action-btn"
            onClick={() => navigate('/results')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 22px', borderRadius: '10px', cursor: 'pointer',
              background: 'transparent', border: `1.5px solid ${C.border}`,
              color: C.navy, fontSize: '0.83rem', fontWeight: '700',
            }}
          >
            <i className="pi pi-chart-bar" style={{ fontSize: '0.8rem' }} />
            Results
          </button>
          <button
            className="sp-action-btn"
            onClick={fetchProfile}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 22px', borderRadius: '10px', cursor: 'pointer',
              background: 'transparent', border: `1.5px solid ${C.border}`,
              color: C.sub, fontSize: '0.83rem', fontWeight: '600',
            }}
          >
            <i className="pi pi-refresh" style={{ fontSize: '0.8rem' }} />
            Refresh
          </button>
        </div>

      </div>
    </>
  );
};

export default StudentProfile;